import { IMetadataApi } from '@kaoto/kaoto';

import { Draft } from '../domain';
export type MappingDocument = Pick<Draft, 'xslt' | 'resources' | 'mapperMetadata'>;

/** PostgreSQL JSONB reorders object keys; that must not make an unchanged draft look edited. */
export const mappingFingerprint = (document: MappingDocument): string => {
  const normalize = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(normalize);
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => [key, normalize(item)]),
    );
  };
  return JSON.stringify(normalize(document));
};

/** Kaoto's own empty body: the mapper shows nothing on that side, with its button to attach a schema. */
export const emptySourceBody = () => ({ type: 'Primitive', filePath: [] as string[], fieldTypeOverrides: [] });

/**
 * Whether a mapping already has the vendor's schema attached: its source body names a file the
 * mapping carries. Anything else opens empty.
 */
export const hasAttachedSource = (document: { resources?: Record<string, string>; mapperMetadata?: unknown }) => {
  const body = (document.mapperMetadata as { sourceBody?: { type?: string; filePath?: string[] } } | undefined)
    ?.sourceBody;
  const file = body?.filePath?.[0];
  return Boolean(body && body.type !== 'Primitive' && file && document.resources?.[file]);
};

/**
 * The vendor's schema the mapper used to guess from sample bills and write as `source.json` on
 * every open. On an endpoint nothing was ever saved from, it is a leftover, not an attachment: a
 * schema attached in the mapper keeps the name of the file it came from. Once a draft exists its
 * mapping points into that file, so it is kept and only Detach schema clears it.
 */
export const isGuessedSource = (document: { mapperMetadata?: unknown }, saved: boolean): boolean =>
  !saved &&
  (document.mapperMetadata as { sourceBody?: { filePath?: string[] } } | undefined)?.sourceBody?.filePath?.[0] ===
    'source.json';

export class MappingSession implements IMetadataApi {
  readonly shouldSaveSchema = true;
  askUserForFileSelection: IMetadataApi['askUserForFileSelection'] = async () => undefined;
  getSuggestions: IMetadataApi['getSuggestions'] = async () => [];
  onStepUpdated: IMetadataApi['onStepUpdated'] = async () => {};
  private readonly listeners = new Set<() => void>();
  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  private metadata: unknown;
  private resources: Record<string, string>;
  private readonly stylesheet = 'mapping.xsl';

  /**
   * `source` is the vendor's schema when one is already known. Without one the source body starts
   * empty and is filled by attaching a schema in the mapper; it is never guessed.
   */
  constructor(
    initial: MappingDocument,
    source: unknown,
    target: unknown,
    private readonly changed: (next: MappingDocument) => void,
  ) {
    this.resources = {
      ...(source === undefined ? {} : { 'source.json': JSON.stringify(source) }),
      'target.json': JSON.stringify(target),
      ...initial.resources,
      'mapping.xsl': initial.xslt ?? '',
    };
    this.metadata = initial.mapperMetadata ?? {
      xsltPath: this.stylesheet,
      sourceParameters: {},
      sourceBody: source === undefined ? emptySourceBody() : { type: 'JSON Schema', filePath: ['source.json'] },
      targetBody: { type: 'JSON Schema', filePath: ['target.json'] },
    };
  }

  private update() {
    this.listeners.forEach((listener) => {
      listener();
    });
    const path = (this.metadata as { xsltPath?: string })?.xsltPath ?? this.stylesheet;
    this.changed({
      xslt: this.resources[path],
      resources: { ...this.resources },
      mapperMetadata: structuredClone(this.metadata),
    });
  }
  snapshot(): MappingDocument {
    const path = (this.metadata as { xsltPath?: string })?.xsltPath ?? this.stylesheet;
    return {
      xslt: this.resources[path],
      resources: { ...this.resources },
      mapperMetadata: structuredClone(this.metadata),
    };
  }
  setStylesheet(content: string) {
    const path = (this.metadata as { xsltPath?: string })?.xsltPath ?? this.stylesheet;
    void this.saveResourceContent(path, content);
  }
  /** The endpoint's main mapping step; every other DataMapper step of its flow keeps its own. */
  primaryKey?: string;

  private stepKey(key?: string) {
    return key && this.primaryKey && key !== this.primaryKey ? `datamapper/${key}.json` : undefined;
  }

  async getMetadata<T>(key?: string): Promise<T | undefined> {
    const own = this.stepKey(key);
    if (!own) return structuredClone(this.metadata) as T;
    const held = this.resources[own];
    if (held) return JSON.parse(held) as T;
    const main = this.metadata as { sourceBody?: unknown; targetBody?: unknown } | undefined;
    return {
      xsltPath: `${key}.xsl`,
      sourceParameters: {},
      sourceBody: structuredClone(main?.sourceBody) ?? emptySourceBody(),
      targetBody: structuredClone(main?.targetBody) ?? { type: 'JSON Schema', filePath: ['target.json'] },
      namespaceMap: {},
    } as T;
  }

  /**
   * A step Kaoto has just opened for the first time starts with no shapes; it is given the main
   * mapping's vendor and Casa shapes, so a branch maps the same bill to the same format.
   */
  async setMetadata<T>(key: string, metadata: T): Promise<void> {
    const own = this.stepKey(key);
    if (!own) {
      if (JSON.stringify(this.metadata) === JSON.stringify(metadata)) return;
      this.metadata = structuredClone(metadata);
      this.update();
      return;
    }
    const main = this.metadata as { sourceBody?: unknown; targetBody?: unknown } | undefined;
    const step = structuredClone(metadata) as {
      sourceBody?: { filePath?: string[] };
      targetBody?: { filePath?: string[] };
    };
    if (!step.sourceBody?.filePath?.length && main?.sourceBody)
      step.sourceBody = structuredClone(main.sourceBody) as never;
    if (!step.targetBody?.filePath?.length && main?.targetBody)
      step.targetBody = structuredClone(main.targetBody) as never;
    await this.saveResourceContent(own, JSON.stringify(step));
  }
  async getResourceContent(path: string): Promise<string | undefined> {
    return this.resources[path];
  }
  async isResourceExist(path: string): Promise<boolean> {
    return path in this.resources;
  }
  async saveResourceContent(path: string, content: string): Promise<void> {
    if (this.resources[path] === content) return;
    this.resources[path] = content;
    this.update();
  }
  async deleteResource(path: string): Promise<boolean> {
    const exists = path in this.resources;
    delete this.resources[path];
    this.update();
    return exists;
  }
}

let active: { key: string; session: MappingSession } | undefined;
const listeners = new Set<() => void>();
export const subscribeActiveMapping = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const activeMapping = () => active;
export const selectMapping = (key: string, session: MappingSession) => {
  session.primaryKey = key;
  active = { key, session };
  listeners.forEach((listener) => {
    listener();
  });
};
export const clearActiveMapping = () => {
  active = undefined;
  listeners.forEach((listener) => {
    listener();
  });
};
